import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError, api } from '@shared/api';
import { idr, ms, num, relative } from '@shared/format';
import { refreshBalance } from '@shared/hooks';
import { Alert, Card, CodeBlock, CopyButton, Icon, InfoTip, KeyValue, Segmented, Tabs } from '../../components/ui';
import { ModelPicker } from '../../components/ModelPicker';
import { friendlyError } from '../../errors';
import { LANGS, VIDEO_LANGS, imageSnippet, videoSnippet, type Lang } from '../../snippets';
import type { CatalogModel } from '../../catalog';

interface MediaProps {
  models: CatalogModel[];
  model: string;
  onModelChange: (id: string) => void;
  projectId: string;
  projectField: ReactNode;
}

function toError(err: unknown): ReactNode {
  return err instanceof ApiError ? friendlyError(err.type, err.message, err.requestId) : (err as Error).message;
}

function runOnModEnter(run: () => void) {
  return (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      run();
    }
  };
}

/** Broken or expired preview. */
function MediaFallback({ href, ratio }: { href: string; ratio?: string }) {
  return (
    <div className="media-fallback" style={ratio ? { aspectRatio: ratio, minHeight: 0 } : undefined}>
      <span>
        Pratinjau tidak bisa dimuat. Tautannya mungkin sudah kedaluwarsa.
        <br />
        <a href={href} target="_blank" rel="noreferrer">
          Buka tautan
        </a>
      </span>
    </div>
  );
}

const SIZES: ReadonlyArray<{ id: string; label: string; ratio: string }> = [
  { id: '2048x2048', label: '1:1', ratio: '1 / 1' },
  { id: '2304x1728', label: '4:3', ratio: '4 / 3' },
  { id: '1728x2304', label: '3:4', ratio: '3 / 4' },
  { id: '2560x1440', label: '16:9', ratio: '16 / 9' },
  { id: '1440x2560', label: '9:16', ratio: '9 / 16' },
];

interface ImageBatch {
  id: number;
  model: string;
  prompt: string;
  size: string;
  images: Array<{ url?: string; b64_json?: string }>;
  cost: number;
  latency: number;
  at: number;
}

function ImageTile({ src, index, batch, ratio }: { src: string; index: number; batch: ImageBatch; ratio: string }) {
  const [broken, setBroken] = useState(false);
  return (
    <figure className="pgm-tile">
      {broken ? (
        <MediaFallback href={src} ratio={ratio} />
      ) : (
        <a href={src} target="_blank" rel="noreferrer" className="pgm-img" style={{ aspectRatio: ratio }}>
          <img src={src} alt={`Hasil ${index + 1}: ${batch.prompt}`} onError={() => setBroken(true)} />
        </a>
      )}
      <figcaption>
        <span className="muted">Gambar {index + 1}</span>
        <span className="grow" />
        <a className="btn btn-sm btn-ghost" href={src} download={`aiagg-${batch.model}-${batch.id}-${index + 1}`}>
          Unduh
        </a>
        <a className="btn btn-sm btn-ghost" href={src} target="_blank" rel="noreferrer">
          Buka
          <Icon name="external" size={14} />
        </a>
      </figcaption>
    </figure>
  );
}

let batchSeq = 0;

export function ImagePlayground({ models, model, onModelChange, projectId, projectField }: MediaProps) {
  const [prompt, setPrompt] = useState('Poster kopi susu gula aren, gaya ilustrasi flat, warna hangat');
  const [n, setN] = useState(1);
  const [size, setSize] = useState(SIZES[0].id);
  const [batches, setBatches] = useState<ImageBatch[]>([]);
  const [error, setError] = useState<ReactNode>(null);
  const [running, setRunning] = useState(false);
  const [lang, setLang] = useState<Lang>('python');
  const selected = models.find((m) => m.id === model);
  const maxN = Math.max(1, Math.min(4, selected?.max_output_tokens ?? 4));
  const perImage = selected?.pricing?.per_image;
  const ratioOf = (id: string) => SIZES.find((s) => s.id === id)?.ratio ?? '1 / 1';
  const nameOf = (id: string) => models.find((m) => m.id === id)?.display_name ?? id;
  const last = batches[0];

  useEffect(() => {
    if (n > maxN) setN(maxN);
  }, [n, maxN]);

  const run = async () => {
    if (running || !prompt.trim() || !model) return;
    setRunning(true);
    setError(null);
    const started = performance.now();
    try {
      const result = await api<{ data: Array<{ url?: string; b64_json?: string }>; billing: { amount: number } }>(
        '/v1/playground/images/generations',
        { method: 'POST', body: { model, prompt, n, size, project_id: projectId || undefined } },
      );
      setBatches((current) => [
        {
          id: ++batchSeq,
          model,
          prompt,
          size,
          images: result.data,
          cost: result.billing?.amount ?? 0,
          latency: performance.now() - started,
          at: Date.now(),
        },
        ...current,
      ]);
      refreshBalance();
    } catch (err) {
      setError(toError(err));
    } finally {
      setRunning(false);
    }
  };

  if (models.length === 0) return <Alert kind="info">Belum ada model gambar yang aktif.</Alert>;

  return (
    <div className="split">
      <div className="col pgm-main">
        <div className="card pgm-compose">
          <div className="pg-picker">
            <ModelPicker models={models} value={model} onChange={onModelChange} disabled={running} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="img-prompt">Deskripsi gambar</label>
            <textarea
              id="img-prompt"
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={runOnModEnter(() => void run())}
              placeholder="Sebutkan objek, gaya, warna, dan komposisi yang Anda inginkan."
            />
          </div>
          <div className="pgm-actions">
            <span className="pgm-cost">
              {perImage !== undefined ? (
                <>
                  {n} × {idr(perImage)} = <b>{idr(perImage * n)}</b>
                </>
              ) : (
                'Harga belum tersedia'
              )}
            </span>
            <span className="grow" />
            <span className="pg-hint">
              <kbd>Ctrl</kbd> + <kbd>Enter</kbd>
            </span>
            <button className="btn btn-primary" onClick={() => void run()} disabled={running || !prompt.trim() || !model}>
              {running ? <span className="spinner" /> : <Icon name="spark" size={16} />}
              {running ? 'Membuat…' : n > 1 ? `Buat ${n} gambar` : 'Buat gambar'}
            </button>
          </div>
        </div>

        {error && <Alert kind="error">{error}</Alert>}

        {running && (
          <div className="card">
            <div className="pgm-batch-head">
              <span className="spinner" />
              <span className="secondary">
                Membuat {n} gambar dengan {nameOf(model)}…
              </span>
            </div>
            <div className="pgm-gallery">
              {Array.from({ length: n }, (_, i) => (
                <div key={i} className="skeleton" style={{ aspectRatio: ratioOf(size) }} />
              ))}
            </div>
          </div>
        )}

        {batches.length === 0 && !running ? (
          <div className="pgm-empty">
            <span className="pgm-empty-icon">
              <Icon name="image" size={20} />
            </span>
            <strong>Hasil gambar tampil di sini</strong>
            <span>Semua hasil di sesi ini tersimpan sampai halaman ditutup.</span>
          </div>
        ) : (
          batches.map((batch) => (
            <div key={batch.id} className="card">
              <div className="pgm-batch-head">
                <span className="pgm-batch-prompt" title={batch.prompt}>
                  {batch.prompt}
                </span>
                <span className="pgm-batch-meta">
                  {nameOf(batch.model)} · {batch.size.replace('x', '×')} · {idr(batch.cost, true)} · {relative(new Date(batch.at))}
                </span>
              </div>
              <div className="pgm-gallery">
                {batch.images.map((image, index) => (
                  <ImageTile
                    key={index}
                    index={index}
                    batch={batch}
                    ratio={ratioOf(batch.size)}
                    src={image.url ?? `data:image/png;base64,${image.b64_json}`}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="col pg-rail">
        <Card title="Pengaturan">
          {projectField}
          <div className="field">
            <span className="label label-tip">
              Jumlah gambar
              <InfoTip label="Tentang jumlah gambar">
                Berapa variasi gambar yang dibuat dari satu deskripsi. Biaya dihitung per gambar yang jadi.
              </InfoTip>
            </span>
            <Segmented
              label="Jumlah gambar"
              options={Array.from({ length: maxN }, (_, i) => ({ id: String(i + 1), label: String(i + 1) }))}
              value={String(n)}
              onChange={(v) => setN(Number(v))}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <span className="label label-tip">
              Rasio
              <InfoTip label="Tentang rasio gambar">
                Perbandingan lebar dan tinggi. 1:1 untuk feed, 16:9 untuk banner atau layar lebar, 9:16 untuk story. Harga sama untuk
                semua rasio.
              </InfoTip>
            </span>
            <Segmented label="Rasio" options={SIZES} value={size} onChange={setSize} />
            <span className="field-hint">{size.replace('x', ' × ')} piksel</span>
          </div>
        </Card>

        <Card title="Run terakhir">
          {last ? (
            <KeyValue
              items={[
                ['Model', nameOf(last.model)],
                ['Gambar', `${last.images.length} · ${last.size.replace('x', ' × ')}`],
                ['Biaya', <b>{idr(last.cost, true)}</b>],
                ['Waktu', ms(last.latency)],
              ]}
            />
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              Biaya dan waktu proses tampil setelah gambar pertama jadi.
            </p>
          )}
        </Card>

        <CodeBlock code={imageSnippet(lang, { model, prompt })} tabs={<Tabs tight tabs={LANGS} value={lang} onChange={setLang} />} />
      </div>
    </div>
  );
}

interface VideoState {
  id: string;
  status: string;
  model?: string;
  created_at?: number;
  completed_at?: number | null;
  direct_url?: string | null;
  url?: string | null;
  error?: { code: string; message: string } | null;
  billing?: { amount: number } | null;
  usage?: { video_tokens: number } | null;
}

const VIDEO_PIXELS: Record<string, number> = { '480p': 428_544, '720p': 927_408, '1080p': 2_086_876 };
const RESOLUTIONS = ['480p', '720p', '1080p'] as const;
const RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'] as const;
const LIVE = ['queued', 'in_progress'];

const STEPS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'queued', label: 'Dalam antrean' },
  { id: 'in_progress', label: 'Diproses' },
  { id: 'done', label: 'Selesai' },
];

const STATUS_TEXT: Record<string, string> = {
  queued: 'Menunggu giliran di provider',
  in_progress: 'Video sedang dibuat',
  completed: 'Video siap',
  failed: 'Gagal dibuat',
  cancelled: 'Dibatalkan',
  expired: 'Kedaluwarsa',
};

function clock(msValue: number) {
  const total = Math.max(0, Math.floor(msValue / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Text to video, polled. */
export function VideoPlayground({ models, model, onModelChange, projectId, projectField }: MediaProps) {
  const [prompt, setPrompt] = useState('Ombak di Pantai Kuta saat senja, kamera bergerak pelan');
  const [seconds, setSeconds] = useState(5);
  const [resolution, setResolution] = useState<string>('720p');
  const [ratio, setRatio] = useState<string>('16:9');
  const [video, setVideo] = useState<VideoState | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [marks, setMarks] = useState<Record<string, number>>({});
  const [now, setNow] = useState(Date.now());
  const [creating, setCreating] = useState(false);
  const [broken, setBroken] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  const [lang, setLang] = useState<Lang>('python');
  const timer = useRef<number | null>(null);
  const selected = models.find((m) => m.id === model);
  const maxSeconds = selected?.max_output_tokens ?? 15;
  const busy = creating || (video !== null && LIVE.includes(video.status));
  const durations = [5, 10, 15, 30].filter((d) => d <= maxSeconds);

  const estimateTokens = Math.ceil((seconds * (VIDEO_PIXELS[resolution] ?? VIDEO_PIXELS['720p']) * 24) / 1024);
  const estimate = selected?.pricing?.per_1m_video_tokens !== undefined ? (estimateTokens * selected.pricing.per_1m_video_tokens) / 1e6 : null;

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    if (seconds > maxSeconds) setSeconds(Math.min(5, maxSeconds));
  }, [seconds, maxSeconds]);

  useEffect(() => {
    if (!busy) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [busy]);

  const mark = (status: string, start: number) => {
    const key = LIVE.includes(status) ? status : 'done';
    setMarks((current) => (key in current ? current : { ...current, [key]: Date.now() - start }));
  };

  const poll = (id: string, start: number) => {
    timer.current = window.setTimeout(async () => {
      try {
        const next = await api<VideoState>(`/v1/playground/videos/${id}`);
        setVideo(next);
        mark(next.status, start);
        if (LIVE.includes(next.status)) poll(id, start);
        else {
          setNow(Date.now());
          refreshBalance();
        }
      } catch (err) {
        setError(toError(err));
      }
    }, 2_000);
  };

  const run = async () => {
    if (busy || !prompt.trim() || !model) return;
    if (timer.current) window.clearTimeout(timer.current);
    setError(null);
    setVideo(null);
    setBroken(false);
    setMarks({});
    setCreating(true);
    const start = Date.now();
    setStartedAt(start);
    setNow(start);
    try {
      const created = await api<VideoState>('/v1/playground/videos', {
        method: 'POST',
        body: { model, prompt, seconds, resolution, aspect_ratio: ratio, project_id: projectId || undefined },
      });
      setVideo(created);
      mark(created.status, start);
      refreshBalance();
      if (LIVE.includes(created.status)) poll(created.id, start);
    } catch (err) {
      setError(toError(err));
      setStartedAt(null);
    } finally {
      setCreating(false);
    }
  };

  if (models.length === 0) return <Alert kind="info">Belum ada model video yang aktif.</Alert>;

  const status = video?.status ?? (creating ? 'queued' : null);
  const failed = status !== null && !LIVE.includes(status) && status !== 'completed';
  const stepIndex = status === null ? -1 : status === 'queued' ? 0 : status === 'in_progress' ? 1 : 2;
  const elapsed = startedAt ? (marks.done ?? now - startedAt) : 0;

  return (
    <div className="split">
      <div className="col pgm-main">
        <div className="card pgm-compose">
          <div className="pg-picker">
            <ModelPicker models={models} value={model} onChange={onModelChange} disabled={busy} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="vid-prompt">Deskripsi video</label>
            <textarea
              id="vid-prompt"
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={runOnModEnter(() => void run())}
              placeholder="Jelaskan adegan, gerakan kamera, dan suasananya."
            />
          </div>
          <div className="pgm-actions">
            <span className="pgm-cost">
              {estimate !== null ? (
                <>
                  Perkiraan <b>{idr(estimate)}</b> · {num(estimateTokens)} token video
                </>
              ) : (
                'Harga belum tersedia'
              )}
            </span>
            <span className="grow" />
            <span className="pg-hint">
              <kbd>Ctrl</kbd> + <kbd>Enter</kbd>
            </span>
            <button className="btn btn-primary" onClick={() => void run()} disabled={busy || !prompt.trim() || !model}>
              {busy ? <span className="spinner" /> : <Icon name="video" size={16} />}
              {busy ? 'Sedang dibuat…' : 'Buat video'}
            </button>
          </div>
        </div>

        {error && <Alert kind="error">{error}</Alert>}

        <div className="card pgm-video">
          <div className="pgm-timeline">
            <ol className="pgm-steps">
              {STEPS.map((step, index) => {
                const reached = stepIndex >= index;
                const current = stepIndex === index && LIVE.includes(status ?? '');
                const bad = index === 2 && failed;
                const cls = ['pgm-step', reached ? 'reached' : '', current ? 'current' : '', bad ? 'bad' : ''].filter(Boolean).join(' ');
                const at = marks[step.id];
                return (
                  <li key={step.id} className={cls}>
                    <span className="pgm-step-dot">
                      {current ? <span className="spinner" /> : bad ? <Icon name="close" size={13} /> : reached ? <Icon name="check" size={13} /> : index + 1}
                    </span>
                    <span className="pgm-step-label">{bad ? STATUS_TEXT[status ?? ''] ?? status : step.label}</span>
                    {at !== undefined && <span className="pgm-step-time">+{clock(at)}</span>}
                  </li>
                );
              })}
            </ol>
            <div className="pgm-clock">
              <span>Waktu</span>
              <b>{clock(elapsed)}</b>
            </div>
          </div>

          {status === null ? (
            <div className="pgm-empty" style={{ aspectRatio: '16 / 9', minHeight: 0 }}>
              <span className="pgm-empty-icon">
                <Icon name="video" size={20} />
              </span>
              <strong>Video biasanya jadi dalam 30 detik sampai beberapa menit</strong>
              <span>Saldo ditahan sesuai perkiraan dan baru dipotong setelah video jadi.</span>
            </div>
          ) : video?.status === 'completed' && video.direct_url ? (
            broken ? (
              <MediaFallback href={video.direct_url} ratio="16 / 9" />
            ) : (
              <video className="pgm-player" src={video.direct_url} controls autoPlay muted loop onError={() => setBroken(true)} />
            )
          ) : failed ? (
            <Alert kind="error">
              {STATUS_TEXT[status ?? ''] ?? status}
              {video?.error?.message ? `: ${video.error.message}` : ''}. Saldo yang ditahan sudah dikembalikan.
            </Alert>
          ) : (
            <div className="pgm-empty pgm-wait" style={{ aspectRatio: '16 / 9', minHeight: 0 }}>
              <span className="spinner" style={{ width: 22, height: 22 }} />
              <strong>{STATUS_TEXT[status ?? 'queued']}</strong>
              <span>Status dicek otomatis setiap 2 detik.</span>
            </div>
          )}

          {video?.status === 'completed' && video.direct_url && (
            <div className="pgm-video-foot">
              <a className="btn btn-sm" href={video.direct_url} download target="_blank" rel="noreferrer">
                <Icon name="arrow" size={14} style={{ transform: 'rotate(90deg)' }} />
                Unduh MP4
              </a>
              <span className="muted small">Tautan berlaku 24 jam.</span>
              <span className="grow" />
              {video.billing && (
                <span className="pgm-cost">
                  Biaya <b>{idr(video.billing.amount, true)}</b>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="col pg-rail">
        <Card title="Pengaturan">
          {projectField}
          <div className="field">
            <span className="label label-tip">
              Durasi
              <InfoTip label="Tentang durasi">Panjang video. Biaya naik sebanding dengan durasi.</InfoTip>
            </span>
            <Segmented
              label="Durasi"
              options={durations.map((d) => ({ id: String(d), label: `${d} detik` }))}
              value={String(seconds)}
              onChange={(v) => setSeconds(Number(v))}
            />
            <span className="field-hint">Maksimal {maxSeconds} detik untuk model ini.</span>
          </div>
          <div className="field">
            <span className="label label-tip">
              Resolusi
              <InfoTip label="Tentang resolusi">
                Ketajaman video. 1080p kira-kira dua kali biaya 720p dan butuh waktu proses lebih lama; 480p cukup untuk draf.
              </InfoTip>
            </span>
            <Segmented label="Resolusi" options={RESOLUTIONS.map((r) => ({ id: r, label: r }))} value={resolution} onChange={setResolution} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <span className="label label-tip">
              Rasio
              <InfoTip label="Tentang rasio video">
                Perbandingan lebar dan tinggi. 16:9 untuk YouTube atau presentasi, 9:16 untuk Reels dan TikTok, 1:1 untuk feed.
              </InfoTip>
            </span>
            <Segmented label="Rasio" options={RATIOS.map((r) => ({ id: r, label: r }))} value={ratio} onChange={setRatio} />
          </div>
        </Card>

        <Card title="Detail video">
          {video ? (
            <>
              <KeyValue
                items={[
                  ['Status', STATUS_TEXT[video.status] ?? video.status],
                  ['Token video', video.usage ? num(video.usage.video_tokens) : '—'],
                  ['Biaya', <b>{video.billing ? idr(video.billing.amount, true) : 'Menunggu'}</b>],
                ]}
              />
              <div className="pg-reqid">
                <code className="grow truncate">{video.id}</code>
                <CopyButton value={video.id} label="Salin ID" />
              </div>
            </>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              ID, token video, dan biaya final tampil setelah video dibuat.
            </p>
          )}
        </Card>

        <CodeBlock
          code={videoSnippet(lang === 'php' ? 'python' : lang, { model, prompt })}
          tabs={<Tabs tight tabs={VIDEO_LANGS} value={lang === 'php' ? 'python' : lang} onChange={setLang} />}
        />
      </div>
    </div>
  );
}
